import { FC, useRef, useEffect, useState } from 'react';
import { TextField } from '../components/TextField';
import { PhoneField } from '../contact-methods/PhoneField';
import { isPlatform } from '../utils/isPlatform';
import { createUseStyles } from 'react-jss';

export interface IProfileCollectionData {
    firstName: string;
    lastName: string;
    zipCode: string;
    phone: string;
}

type IProfileCollectionFormProps = {
    data: IProfileCollectionData;
    onChange: (data: IProfileCollectionData) => void;
    onValidityChange?: (isValid: boolean) => void;
    autoFocus?: boolean;
};

const useStyles = createUseStyles({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        width: '100%',
        maxWidth: 400
    }
});

export const ProfileCollectionForm: FC<IProfileCollectionFormProps> = ({ data, onChange, onValidityChange, autoFocus }) => {
    const classes = useStyles();
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    const firstNameRef = useRef<HTMLIonInputElement>(null);

    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (isPlatform('desktop') || isPlatform('capacitor')) {
            const timer = setTimeout(() => {
                firstNameRef.current?.setFocus();
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [autoFocus]);

    useEffect(() => {
        const isValid = Boolean(data.firstName.trim()) && /^\d{5}$/.test(data.zipCode) && isPhoneValid;
        onValidityChange?.(isValid);
    }, [data.firstName, data.zipCode, isPhoneValid, onValidityChange]);

    return (
        <div className={classes.wrapper}>
            <TextField
                inputRef={firstNameRef}
                name="first-name"
                label="First Name"
                value={data.firstName}
                autocapitalize="words"
                required
                onInput={firstName => onChange({ ...data, firstName })}
            />
            <TextField
                name="last-name"
                label="Last Name"
                value={data.lastName}
                autocapitalize="words"
                onInput={lastName => onChange({ ...data, lastName })}
            />
            <TextField
                name="zip-code"
                label="Zip Code"
                value={data.zipCode}
                placeholder="12345"
                type="text"
                inputmode="numeric"
                maxLength={5}
                required
                onInput={zipCode => onChange({ ...data, zipCode: zipCode.replace(/\D/g, '').slice(0, 5) })}
            />
            <PhoneField
                required
                isValid={setIsPhoneValid}
                method={{ type: 'mobile-phone', value: data.phone }}
                methodChanged={method => onChange({ ...data, phone: method.value })}
            />
        </div>
    );
};
